import { useEffect, useRef } from 'react';
import type { CbullshitcodingContentStateControls } from './CbullshitcodingContentState';

interface UseCbullshitcodingPropStringsFadeInput
  extends Pick<
    CbullshitcodingContentStateControls,
    'fadeDurationMs' | 'fadeTimeoutRef' | 'hasContentRef' | 'resetTypingState' | 'setIsFadingOut'
  > {
  codeFile?: string;
  contentKey?: string;
  propStrings?: string[];
}

function didPropStringsChange(input: {
  propStrings?: string[];
  previousPropStrings?: string[];
}): boolean {
  return input.propStrings !== input.previousPropStrings;
}

function hasRenderablePropStrings(input: {
  propStrings?: string[];
}): boolean {
  return !!input.propStrings && input.propStrings.length > 0;
}

function shouldFadePropStringsChange(input: {
  propStrings?: string[];
  codeFile?: string;
  hasContent: boolean;
  contentKey?: string;
}): boolean {
  if (input.codeFile) {
    return false;
  }

  if (!hasRenderablePropStrings({ propStrings: input.propStrings })) {
    return false;
  }

  return input.hasContent && input.contentKey == null;
}

function startPropStringsFade(input: {
  fadeDurationMs: number;
  fadeTimeoutRef: UseCbullshitcodingPropStringsFadeInput['fadeTimeoutRef'];
  resetTypingState: () => void;
  setIsFadingOut: UseCbullshitcodingPropStringsFadeInput['setIsFadingOut'];
}): void {
  input.setIsFadingOut(true);
  input.fadeTimeoutRef.current = setTimeout(() => {
    input.resetTypingState();
    input.setIsFadingOut(false);
  }, input.fadeDurationMs);
}

export function useCbullshitcodingPropStringsFade({
  codeFile,
  contentKey,
  fadeDurationMs,
  fadeTimeoutRef,
  hasContentRef,
  propStrings,
  resetTypingState,
  setIsFadingOut,
}: UseCbullshitcodingPropStringsFadeInput): void {
  const prevPropStringsRef = useRef(propStrings);

  useEffect(() => {
    if (!didPropStringsChange({
      propStrings,
      previousPropStrings: prevPropStringsRef.current,
    })) {
      return;
    }
    prevPropStringsRef.current = propStrings;

    if (!shouldFadePropStringsChange({
      propStrings,
      codeFile,
      hasContent: hasContentRef.current,
      contentKey,
    })) {
      return;
    }

    startPropStringsFade({
      fadeDurationMs,
      fadeTimeoutRef,
      resetTypingState,
      setIsFadingOut,
    });
  }, [
    codeFile,
    contentKey,
    fadeDurationMs,
    fadeTimeoutRef,
    hasContentRef,
    propStrings,
    resetTypingState,
    setIsFadingOut,
  ]);
}
